// Re-export types from types file
export * from './types.js';
import type { LanguageContent, VocabularyCategory } from './types.js';

// Language-specific content imports
import { spanishContent } from './spanish.js';
import { portugueseContent } from './portuguese.js';
import { finnishContent } from './finnish.js';

// Vocabulary data will be loaded lazily to avoid Lambda timeout
let spanishWordsData: any[] | null = null;
let portugueseWordsData: any[] | null = null;

// Content registry
export const languageContent: Record<string, LanguageContent> = {
  es: spanishContent,
  pt: portugueseContent,
  fi: finnishContent,
};

// Helper functions
export function getLanguageContent(code: string): LanguageContent | null {
  return languageContent[code] || null;
}

// Helper function to get vocabulary data (handles both sync and async)
export async function getVocabularyData(code: string): Promise<VocabularyCategory[]> {
  const content = languageContent[code];
  if (!content) return [];
  
  if (typeof content.vocabulary === 'function') {
    return await content.vocabulary();
  }
  
  return content.vocabulary;
}

export function getAvailableLanguages(): string[] {
  return Object.keys(languageContent);
}

export function stripAccents(input: string): string {
  return input.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

// Vocabulary lookup types
export type VocabularyEntry = {
  word: string;
  useful_for_flashcard: boolean;
  cefr_level: string;
  english_translation: string;
  example_sentence_native: string;
  example_sentence_english: string;
  pos: string;
  word_frequency: number;
  cefr_update: string;
  // Portuguese-specific fields
  romanization?: string;
  language_variety?: string;
};

// Lazy loading functions for vocabulary data
async function loadSpanishWordsData(): Promise<any[]> {
  if (!spanishWordsData) {
    try {
      const response = await fetch('/api/vocabulary/es_v2.json');
      if (response.ok) {
        spanishWordsData = await response.json();
      } else {
        throw new Error(`Failed to load Spanish vocabulary: ${response.status}`);
      }
    } catch (error) {
      console.error('Error loading Spanish vocabulary:', error);
      spanishWordsData = [];
    }
  }
  return spanishWordsData!;
}

async function loadPortugueseWordsData(): Promise<any[]> {
  if (!portugueseWordsData) {
    try {
      const response = await fetch('/api/vocabulary/pt_v2.json');
      if (response.ok) {
        portugueseWordsData = await response.json();
      } else {
        throw new Error(`Failed to load Portuguese vocabulary: ${response.status}`);
      }
    } catch (error) {
      console.error('Error loading Portuguese vocabulary:', error);
      portugueseWordsData = [];
    }
  }
  return portugueseWordsData!;
}

let finnishWordsData: any[] | null = null;

async function loadFinnishWordsData(): Promise<any[]> {
  if (!finnishWordsData) {
    try {
      const response = await fetch('/api/vocabulary/fi_v2.json');
      if (response.ok) {
        finnishWordsData = await response.json();
      } else {
        finnishWordsData = [];
      }
    } catch {
      finnishWordsData = [];
    }
  }
  return finnishWordsData!;
}

// Vocabulary lookup function (now async)
export async function lookupVocabularyEntry(word: string, languageCode: string): Promise<VocabularyEntry | null> {
  const data = languageCode === 'es'
    ? await loadSpanishWordsData()
    : languageCode === 'fi'
    ? await loadFinnishWordsData()
    : await loadPortugueseWordsData();
  const entry = (data as any[]).find((item: any) => item.word === word);
  return entry || null;
}
